import type {
  Project,
  Preventivo,
  Finanziamento,
  GanttTask,
  CostoExtra,
} from "../types";
import type { TaxBreakdown } from "./taxCalculations";
import {
  calculateRataFromFinanziamento,
  generateRateScadenze,
} from "./financeCalculations";
import { getImportoStimato } from "../utils/pricing";

export interface CashFlowEvent {
  date: string; // ISO date
  description: string;
  amount: number; // negative = uscita, positive = entrata
  category:
    | "preventivo"
    | "finanziamento"
    | "notaio"
    | "costo_extra"
    | "rata"
    | "altro";
  running_balance?: number;
  dateEstimated?: boolean;
}

export interface CashFlowSummary {
  totalEntrate: number;
  totalUscite: number;
  saldoFinale: number;
  events: CashFlowEvent[];
  byMonth: Map<string, { entrate: number; uscite: number; saldo: number }>;
}

interface CalcoloCashFlowParams {
  project: Project;
  preventivi: Preventivo[];
  finanziamenti: Finanziamento[];
  ganttTasks: GanttTask[];
  costiExtra: CostoExtra[];
  taxBreakdown: TaxBreakdown | null;
  estimateLevel: "min" | "med" | "max";
  dataRogito: string; // ISO date of purchase
}

function getMonthKey(date: string): string {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function parseDecimal(value: string | undefined | null): number {
  if (!value) return 0;
  return parseFloat(value) || 0;
}

export function calcolaCashFlow({
  project,
  preventivi,
  finanziamenti,
  ganttTasks,
  costiExtra,
  taxBreakdown,
  estimateLevel,
  dataRogito,
}: CalcoloCashFlowParams): CashFlowSummary {
  const events: CashFlowEvent[] = [];

  // 1. Costo acquisto immobile (al rogito)
  // Il prezzo sta su Immobile (e sulla scheda Notaio): `project.prezzoImmobile`
  // non è un campo del modello e restava sempre undefined, così l'acquisto non
  // compariva mai tra le uscite.
  const prezzoImmobile =
    parseDecimal(project.immobile?.prezzoAcquisto) ||
    parseDecimal(project.notaio?.prezzoAcquisto) ||
    project.prezzoImmobile ||
    0;
  if (prezzoImmobile > 0 && dataRogito) {
    events.push({
      date: dataRogito,
      description: "Acquisto immobile",
      amount: -prezzoImmobile,
      category: "altro",
    });
  }

  // 2. Erogazione mutuo/prestito (alla data di erogazione, altrimenti al rogito)
  finanziamenti.forEach((f) => {
    const importo = parseDecimal(f.importo);
    if (importo > 0) {
      const erogDate = f.dataErogazione
        ? f.dataErogazione.split("T")[0]
        : dataRogito || new Date().toISOString().split("T")[0];
      events.push({
        date: erogDate,
        description: `Erogazione ${f.tipo}: ${f.nome}`,
        amount: importo,
        category: "finanziamento",
      });
    }
  });

  // 3. Costi notaio e tasse (al rogito)
  //
  // Attenzione al doppio conteggio: quando il rogito è già avvenuto, gli importi
  // pagati sono registrati come costi extra (categoria "rogito") con la loro data
  // reale. In quel caso la stima del taxBreakdown va lasciata fuori, altrimenti
  // le stesse somme entrerebbero due volte nelle uscite.
  const rogitoGiaPagato = costiExtra.some(
    (c) => (c.categoria ?? "").toLowerCase() === "rogito",
  );

  if (taxBreakdown && dataRogito && !rogitoGiaPagato) {
    const totaleNotaio =
      taxBreakdown.totaleImposte + taxBreakdown.totaleCostiNotarili;
    if (totaleNotaio > 0) {
      events.push({
        date: dataRogito,
        description: "Imposte e costi notarili (stima)",
        amount: -totaleNotaio,
        category: "notaio",
      });
    }

    if (taxBreakdown.commissioneAgenzia > 0) {
      events.push({
        date: dataRogito,
        description: "Commissione agenzia (stima)",
        amount: -taxBreakdown.commissioneAgenzia,
        category: "notaio",
      });
    }
  }

  // 4. Preventivi (distribuiti nel tempo se ci sono task correlati, altrimenti al rogito)
  const rogitoPlusMonth = dataRogito ? new Date(dataRogito) : new Date();
  rogitoPlusMonth.setMonth(rogitoPlusMonth.getMonth() + 1);
  const defaultPreventivoDate = rogitoPlusMonth.toISOString().split("T")[0];

  const lavoriSospesi = new Set(
    (project.lavori ?? []).filter((l) => l.attivo === false).map((l) => l.id),
  );

  preventivi.forEach((p) => {
    const haConsuntivo = p.importoReale != null && p.importoReale !== "";
    // Una spesa realmente sostenuta resta un'uscita anche se il lavoro viene sospeso.
    if (
      !haConsuntivo &&
      (p.attivo === false || (p.lavoroId && lavoriSospesi.has(p.lavoroId)))
    )
      return;
    let costo: number;
    if (haConsuntivo) {
      costo = parseDecimal(p.importoReale);
    } else {
      switch (estimateLevel) {
        case "min":
          costo = parseDecimal(p.costoMin);
          break;
        case "max":
          costo = parseDecimal(p.costoMax);
          break;
        default:
          costo = getImportoStimato(p);
      }
    }

    if (costo > 0) {
      // Trova task correlato per data
      const relatedTask = ganttTasks
        .filter((t) => !t.nome.startsWith("[STORICO]"))
        .find(
          (t) =>
            (p.categoria.trim() !== "" &&
              t.nome.toLowerCase().includes(p.categoria.toLowerCase())) ||
            (p.voce.trim() !== "" &&
              t.nome.toLowerCase().includes(p.voce.toLowerCase())),
        );

      const dataReale = haConsuntivo
        ? p.dataPagamento?.split("T")[0]
        : undefined;

      events.push({
        date:
          dataReale ||
          relatedTask?.dataInizio?.split("T")[0] ||
          defaultPreventivoDate,
        description: `${p.categoria}: ${p.voce}`,
        amount: -costo,
        category: "preventivo",
        dateEstimated: !dataReale,
      });
    }
  });

  // 5. Costi extra
  costiExtra.forEach((ce) => {
    const importo = parseDecimal(ce.importo);
    if (importo > 0) {
      events.push({
        date: ce.data,
        description: ce.descrizione,
        amount: -importo,
        category: "costo_extra",
      });
    }
  });

  // 6. Rate mensili mutuo/prestito
  finanziamenti.forEach((f) => {
    if (f.tipo === "fondi_propri") return;
    const rataMensile = calculateRataFromFinanziamento(f);
    if (rataMensile <= 0) return;

    if (f.dataPrimaRata) {
      // Piano reale: tutte le rate residue dalla prima rata fino a fine finanziamento
      const scadenze = generateRateScadenze(f);
      scadenze.forEach((rata) => {
        events.push({
          date: rata.date,
          description: `Rata ${rata.numero}/${rata.totaleRate} ${f.nome}`,
          amount: -rata.importo,
          category: "rata",
        });
      });
    } else {
      // Fallback (nessuna data prima rata): proiezione 12 mesi dal rogito
      const startDate = dataRogito ? new Date(dataRogito) : new Date();
      startDate.setMonth(startDate.getMonth() + 1); // Prima rata mese successivo

      for (let i = 0; i < 12; i++) {
        const rataDate = new Date(startDate);
        rataDate.setMonth(rataDate.getMonth() + i);

        events.push({
          date: rataDate.toISOString().split("T")[0],
          description: `Rata ${f.nome}`,
          amount: -rataMensile,
          category: "rata",
        });
      }
    }
  });

  // Sort by date
  events.sort((a, b) => a.date.localeCompare(b.date));

  // Calculate running balance and totals
  let runningBalance = 0;
  let totalEntrate = 0;
  let totalUscite = 0;
  const byMonth = new Map<
    string,
    { entrate: number; uscite: number; saldo: number }
  >();

  events.forEach((event) => {
    if (event.amount > 0) {
      totalEntrate += event.amount;
    } else {
      totalUscite += Math.abs(event.amount);
    }

    runningBalance += event.amount;
    event.running_balance = runningBalance;

    // Aggregate by month
    const monthKey = getMonthKey(event.date);
    const monthData = byMonth.get(monthKey) || {
      entrate: 0,
      uscite: 0,
      saldo: 0,
    };

    if (event.amount > 0) {
      monthData.entrate += event.amount;
    } else {
      monthData.uscite += Math.abs(event.amount);
    }
    monthData.saldo = monthData.entrate - monthData.uscite;

    byMonth.set(monthKey, monthData);
  });

  return {
    totalEntrate,
    totalUscite,
    saldoFinale: runningBalance,
    events,
    byMonth,
  };
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatMonth(monthKey: string): string {
  const [year, month] = monthKey.split("-");
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
}
